import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useElevenLabsTTS = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const isCancelledRef = useRef(false);

  const stopAudio = useCallback(() => {
    // Set cancelled flag to stop any in-progress sequences
    isCancelledRef.current = true;
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    audioQueueRef.current = [];
    isProcessingQueueRef.current = false;
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const playAudio = useCallback((audioUrl) => {
    return new Promise((resolve, reject) => {
      // Check if cancelled before playing
      if (isCancelledRef.current) {
        reject(new Error('Audio cancelled'));
        return;
      }
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsPlaying(false);
        resolve();
      };
      
      audio.onerror = (e) => {
        setIsPlaying(false);
        reject(e);
      };
      
      setIsPlaying(true);
      audio.play().catch(reject);
    });
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    while (audioQueueRef.current.length > 0) {
      if (isCancelledRef.current) {
        break;
      }
      
      const { text, onComplete } = audioQueueRef.current.shift();
      
      try {
        setIsLoading(true);
        const response = await axios.post(`${API}/tts/generate`, { text });
        setIsLoading(false);
        
        if (response.data.audio_url && !isCancelledRef.current) {
          await playAudio(response.data.audio_url);
        }
        
        if (onComplete) {
          onComplete();
        }
      } catch (err) {
        if (err.message !== 'Audio cancelled') {
          console.error('TTS error:', err);
          setError(err.message);
        }
        setIsLoading(false);
      }
    }

    isProcessingQueueRef.current = false;
  }, [playAudio]);

  const speak = useCallback((text, options = {}) => {
    if (!text) return;
    
    // Reset cancelled flag when starting new speech
    isCancelledRef.current = false;

    // Add to queue
    audioQueueRef.current.push({
      text,
      onComplete: options.onEnd
    });

    // Start processing if not already
    processQueue();
  }, [processQueue]);

  const speakSequence = useCallback(async (texts, delayBetween = 2000) => {
    // Reset cancelled flag when starting new sequence
    isCancelledRef.current = false;
    
    for (let i = 0; i < texts.length; i++) {
      // Check if cancelled before each step
      if (isCancelledRef.current) {
        console.log('Voice sequence cancelled');
        break;
      }
      
      const text = texts[i];
      if (!text) continue;

      try {
        setIsLoading(true);
        const response = await axios.post(`${API}/tts/generate`, { text });
        setIsLoading(false);
        
        // Check again after API call
        if (isCancelledRef.current) {
          console.log('Voice sequence cancelled after API call');
          break;
        }
        
        if (response.data.audio_url) {
          await playAudio(response.data.audio_url);
        }
        
        // Wait between segments (but check for cancellation)
        if (i < texts.length - 1 && !isCancelledRef.current) {
          await new Promise(resolve => setTimeout(resolve, delayBetween));
        }
      } catch (err) {
        if (err.message !== 'Audio cancelled') {
          console.error('TTS error:', err);
          setError(err.message);
        }
        setIsLoading(false);
        // Don't break on error - try the next segment
      }
    }
  }, [playAudio]);

  // Speak a single text and return a promise that resolves when done
  const speakAndWait = useCallback(async (text) => {
    if (!text) return;
    
    // Reset cancelled flag
    isCancelledRef.current = false;
    
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/tts/generate`, { text });
      setIsLoading(false);
      
      if (isCancelledRef.current) {
        throw new Error('Audio cancelled');
      }
      
      if (response.data.audio_url) {
        await playAudio(response.data.audio_url);
      }
    } catch (err) {
      setIsLoading(false);
      if (err.message !== 'Audio cancelled') {
        console.error('TTS error:', err);
        setError(err.message);
      }
      throw err;
    }
  }, [playAudio]);

  return {
    speak,
    speakSequence,
    speakAndWait,
    stopAudio,
    isLoading,
    isPlaying,
    isSpeaking: isLoading || isPlaying,
    error
  };
};

export default useElevenLabsTTS;
